// =============================================================================
// IMPORTS
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Grid as MuiGrid,
    Button,
    IconButton,
    CircularProgress,
    Toolbar,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    Checkbox,
    FormControlLabel,
    Tooltip,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    AutoFixHigh as AIIcon,
    Email as EmailIcon,
    Launch as LaunchIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useListView } from '../hooks/useListView';
import { FormLayout, FormField, Record as RecordType } from '../types/form';
import { DATE_FIELD_HINTS } from '../utils/dateFormatter';
import { RelatedListDisplay } from './RelatedListDisplay';
import { AIEnhancementPanel } from './AIEnhancementPanel';
import { AIEnhancementButton } from './AIEnhancementButton';
import { EmailGenerator } from './EmailGenerator';
import api from '../services/api';
import { useRecordForm } from '../hooks/useRecordForm';
import { ImageField } from './ImageField';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================
interface OptionsResult {
    field: string;
    options: any[];
}

interface RecordFormProps {
    tableName?: string;
    previewMode?: boolean;
    previewLayout?: FormLayout;
    fields?: FormField[];
}

type SectionsMap = Record<string, Section>;

interface Section {
    title: string;
    fields: FormField[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`form-tabpanel-${index}`}
            aria-labelledby={`form-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `form-tab-${index}`,
        'aria-controls': `form-tabpanel-${index}`,
    };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function RecordForm({ 
    tableName: tableNameProp, 
    previewMode = false, 
    previewLayout,
    fields: previewFields 
}: RecordFormProps) {
    
    // =============================================================================
    // HOOKS & SETUP
    // =============================================================================
    const params = useParams<{ tableName: string; id?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const tableName = tableNameProp || params.tableName || '';
    const id = params.id;
    const isNew = !id;

    // Parse return path and pre-population parameters from URL
    const returnTable = searchParams.get('returnTable');
    const returnId = searchParams.get('returnId');
    const returnPath = returnTable && returnId ? `/${returnTable}/${returnId}` : `/${tableName}`;

    // In preview mode, use provided fields and layout
    const {
        viewConfig,
        record: existingRecord,
        isLoading,
        error,
        createRecord,
        updateRecord,
        deleteRecord
    } = useListView(tableName || 'preview', id);

    // =============================================================================
    // STATE MANAGEMENT
    // =============================================================================
    const {
        formData,
        setFormData,
        fieldOptions,
        loadingOptions,
        handleChange,
        handleSelectChange,
        handleCheckboxChange,
        handleDateChange
    } = useRecordForm({
        isNew,
        existingRecord,
        tableName,
        searchParams,
        viewConfig,
        previewMode
    });
    const [submitting, setSubmitting] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const [emailGeneratorOpen, setEmailGeneratorOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [relatedListCounts, setRelatedListCounts] = useState<Record<string, number>>({});

    // =============================================================================
    // COMPUTED VALUES
    // =============================================================================
    const fields = previewMode ? previewFields : viewConfig?.fields;
    const layout = useMemo(() => {
        return previewMode ? previewLayout : { layout_config: viewConfig?.layout_config };
    }, [previewMode, previewLayout, viewConfig?.layout_config]);

    const sections = useMemo(() => {
        if (!layout?.layout_config) {
            return {
                default: {
                    title: 'Form Fields',
                    fields: fields || []
                }
            };
        }

        // Parse the layout_config if it's a string
        const layoutConfig = typeof layout.layout_config === 'string' 
            ? JSON.parse(layout.layout_config)
            : layout.layout_config;

        // Create a map of all fields by their field_name
        const fieldsMap = new Map(fields?.map(field => [field.field_name, field]) || []);
        
        // Handle the new simplified layout format where sections contain field names directly
        if (layoutConfig.sections && layoutConfig.sections[0]?.fields && Array.isArray(layoutConfig.sections[0].fields)) {
            return layoutConfig.sections.reduce((acc: SectionsMap, section: { title: string; fields: string[] }, index: number) => {
                // Convert field names to FormField objects
                const sectionFields = section.fields
                    .map((fieldName: string) => fieldsMap.get(fieldName))
                    .filter(Boolean) as FormField[];

                acc[`section_${index}`] = {
                    title: section.title,
                    fields: sectionFields
                };
                return acc;
            }, {} as SectionsMap);
        }

        // Handle the old complex layout format (for backwards compatibility)
        if (layoutConfig.fields) {
            return layoutConfig.sections
                .filter((section: { type?: string }) => section.type !== 'related_list') // Filter out related list sections
                .reduce((acc: SectionsMap, section: { id: string; title: string }) => {
                    // Get fields from layout config for this section
                    const layoutFields = layoutConfig.fields
                        .filter((f: { sectionId: string }) => f.sectionId === section.id)
                        .sort((a: { order?: number }, b: { order?: number }) => (a.order || 0) - (b.order || 0));

                    // Convert layout fields to FormFields
                    const sectionFields = layoutFields.map((layoutField: any) => {
                        const baseField = fieldsMap.get(layoutField.name);
                        if (!baseField) return null;
                        return {
                            ...baseField,
                            ...layoutField,
                            field_name: layoutField.name
                        };
                    }).filter(Boolean) as FormField[];

                    acc[section.id] = {
                        title: section.title,
                        fields: sectionFields
                    };
                    return acc;
                }, {} as SectionsMap);
        }

        // Fallback: create default section
        return {
            default: {
                title: 'Form Fields',
                fields: fields || []
            }
        };
    }, [layout, fields]);

    // =============================================================================
    // EFFECTS
    // =============================================================================
    useEffect(() => {
        if (!isNew && existingRecord) {
            const normalized: RecordType = { ...existingRecord };
            setFormData(normalized);
        } else if (isNew) {
            // Pre-populate form fields from URL parameters
            const initialData: RecordType = {};
            
            // Check for field pre-population from URL parameters
            searchParams.forEach((value, key) => {
                // Skip navigation parameters
                if (key === 'returnTable' || key === 'returnId') return;
                
                // Convert numeric values for ID fields
                if (key.endsWith('_id') || key === 'id') {
                    const numericValue = parseInt(value, 10);
                    if (!isNaN(numericValue)) {
                        initialData[key] = numericValue;
                    } else {
                        initialData[key] = value;
                    }
                } else {
                    initialData[key] = value;
                }
            });
            
            // Auto-populate default thank you message for new thank you notes
            if (tableName === 'thank_you_notes') {
                if (!initialData.note_text) {
                    initialData.note_text = "We wanted to take a moment to say thank you from the bottom of our hearts. Your presence at our wedding meant the world to us, and we're so grateful to have shared such a special day with you.";
                }
                if (!initialData.gift) {
                    initialData.gift = "We also wanted to thank you for your kind and generous gift. Your contribution helped us pay for our honeymoon, and we made some unforgettable memories!";
                }
            }
            
            setFormData(initialData);
        }
    }, [isNew, existingRecord, searchParams, tableName]);

    useEffect(() => {
        if (viewConfig) {
            const editableFields = viewConfig.fields.filter(f => f.editable);
        }
    }, [viewConfig, tableName]);

    // Reset submitting state when navigating between records
    useEffect(() => {
        setSubmitting(false);
    }, [tableName, id]);

    // Fetch related list counts when record ID changes
    useEffect(() => {
        if (!previewMode && formData.id && typeof formData.id === 'number' && layout?.layout_config) {
            let relatedLists: any[] = [];
            try {
                const parsedConfig = typeof layout.layout_config === 'string' 
                    ? JSON.parse(layout.layout_config) 
                    : layout.layout_config;
                relatedLists = parsedConfig.related_lists || [];
            } catch (error) {
                console.error('[RecordForm] Error parsing layout_config for counts:', error);
            }

            if (relatedLists.length > 0) {
                fetchRelatedListCounts(relatedLists, formData.id);
            }
        }
    }, [formData.id, layout?.layout_config, previewMode]);

    // =============================================================================
    // HELPER FUNCTIONS
    // =============================================================================
    /*
     * Option loading logic has been migrated to the useRecordForm hook.
     */

    // =============================================================================
    // EVENT HANDLERS (provided by useRecordForm)
    // =============================================================================

    const handleSubmit = async () => {
        console.log('handleSubmit called', { isNew, formData, tableName });
        setSubmitting(true);
        try {
            if (isNew) {
                const { id, ...dataWithoutId } = formData;
                console.log('Creating new record:', { dataWithoutId, tableName });
                const result = await createRecord.mutateAsync(dataWithoutId);
                console.log('Record created successfully:', result);
            } else {
                console.log('Updating existing record:', { formData, tableName });
                const result = await updateRecord.mutateAsync(formData);
                console.log('Record updated successfully:', result);
            }
            // Stay on the form after saving
            setSubmitting(false);
        } catch (error) {
            console.error('Error saving record:', error);
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Go back to the previous screen
        window.history.back();
    };

    const handleDelete = async () => {
        if (!formData.id || isNew) return;
        
        const confirmed = window.confirm(`Are you sure you want to delete this ${tableName}?`);
        if (!confirmed) return;
        
        try {
            await deleteRecord.mutateAsync(formData.id);
            // Navigate back to the return path (parent form if came from related list)
            navigate(returnPath);
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    // AI Enhancement handlers
    const handleAIFieldUpdate = (fieldName: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleAIBulkUpdate = (updates: Record<string, string>) => {
        setFormData(prev => ({
            ...prev,
            ...updates
        }));
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleNavigateToRelatedRecord = (field: FormField, recordId: string | number) => {
        if (!recordId || !field.options_source) return;
        
        // Navigate to the related record
        // The options_source contains the table name for the related record
        navigate(`/${field.options_source}/${recordId}`);
    };

    // Fetch counts for related lists
    const fetchRelatedListCounts = async (relatedLists: any[], recordId: number) => {
        try {
            const countPromises = relatedLists.map(async (relatedList) => {
                try {
                    // Use the same filtering approach as RelatedListDisplay
                    const params = new URLSearchParams({
                        [`filter[${relatedList.relationshipField}]`]: recordId.toString()
                    });
                    
                    const response = await api.get(`/${relatedList.tableName}?${params}`);
                    const count = Array.isArray(response.data) ? response.data.length : 0;
                    
                    return {
                        id: relatedList.id,
                        count: count
                    };
                } catch (error) {
                    return { id: relatedList.id, count: 0 };
                }
            });

            const results = await Promise.all(countPromises);
            const countsMap = results.reduce((acc, result) => {
                acc[result.id] = result.count;
                return acc;
            }, {} as Record<string, number>);

            setRelatedListCounts(countsMap);
        } catch (error) {
            // Silently handle errors for related list counts
        }
    };

    // =============================================================================
    // COMPUTED DISPLAY VALUES
    // =============================================================================
    const title = tableName.charAt(0).toUpperCase() + tableName.slice(1);
    const formTitle = isNew ? `New ${title}` : `Edit ${title}`;
    const showEmailButton = !isNew && formData.id;

    // =============================================================================
    // FIELD RENDERING
    // =============================================================================
    const renderField = (field: FormField) => {
        const isDateField = field.type === 'date' ||
            DATE_FIELD_HINTS.some(hint => field.field_name.toLowerCase().includes(hint));
        const fieldValue = formData[field.field_name] !== undefined ? formData[field.field_name] : '';
        const isIdField = field.field_name.toLowerCase() === 'id';
        const isReadOnly = !field.editable || isIdField;

        // If it's a non-editable field, render as read-only text field
        if (isReadOnly) {
            return (
                <TextField
                    id={field.field_name}
                    label={field.label}
                    fullWidth
                    value={fieldValue || ''}
                    InputProps={{
                        readOnly: true,
                    }}
                    disabled
                    helperText={field.help_text || (isIdField ? "System generated field" : "This field cannot be edited")}
                />
            );
        }

        switch (field.type) {
            case 'select': {
                let options = fieldOptions[field.field_name] || [];
                // Generic self-referencing select: options_source equals current table
                const isSelfReference = field.options_source === tableName;
                if (isSelfReference && formData.id) {
                    options = options.filter((opt: any) => opt.id !== formData.id);
                }
                
                // Use the configured options_value_field, default to 'id'
                const valueField = field.options_value_field || 'id';
                const displayField = field.options_display_field || 'name';
                
                // Handle case where field has value but options haven't loaded yet
                let safeValue = fieldValue || '';
                const hasOptionsLoaded = field.field_name in fieldOptions;
                if (!hasOptionsLoaded || (loadingOptions && options.length === 0)) {
                    // Use empty value while options are loading to prevent MUI warning
                    safeValue = '';
                } else {
                    safeValue = fieldValue || '';
                }
                
                // Check if we should show navigation button (has value and options_source)
                const showNavigationButton = safeValue && field.options_source;
                
                return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id={`${field.field_name}-label`}>{field.label}</InputLabel>
                            <Select
                                labelId={`${field.field_name}-label`}
                                id={field.field_name}
                                value={safeValue}
                                label={field.label}
                                onChange={handleSelectChange(field)}
                                required={Boolean(field.required)}
                                disabled={submitting || loadingOptions}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {options.map((option: RecordType) => (
                                    <MenuItem key={option[valueField]} value={option[valueField]}>
                                        {option[displayField] || option.name || option.title || option.label || option[valueField]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {showNavigationButton && (
                            <Tooltip title={`View ${field.options_source} record`}>
                                <IconButton
                                    onClick={() => handleNavigateToRelatedRecord(field, safeValue)}
                                    color="primary"
                                    sx={{ mt: 1 }}
                                >
                                    <LaunchIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            }
            case 'textarea':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextField
                            id={field.field_name}
                            label={field.label}
                            multiline
                            rows={4}
                            fullWidth
                            value={fieldValue || ''}
                            onChange={handleChange(field)}
                            required={Boolean(field.required)}
                            disabled={submitting}
                        />
                        {field.ai_editable && (
                            <AIEnhancementButton
                                table={tableName}
                                recordId={typeof formData.id === 'number' ? formData.id : (formData.id ? Number(formData.id) : undefined)}
                                fieldName={field.field_name}
                                currentValue={fieldValue || ''}
                                formData={formData}
                                onEnhancementResult={(enhancedContent) => handleAIFieldUpdate(field.field_name, enhancedContent)}
                                disabled={submitting}
                            />
                        )}
                    </Box>
                );
            case 'boolean':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                id={field.field_name}
                                checked={!!fieldValue}
                                onChange={handleCheckboxChange(field)}
                                disabled={submitting}
                            />
                        }
                        label={field.label}
                    />
                );
            case 'date':
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label={field.label}
                            value={fieldValue ? new Date(fieldValue) : null}
                            onChange={handleDateChange(field)}
                            slotProps={{
                                textField: {
                                    id: field.field_name,
                                    required: Boolean(field.required),
                                    fullWidth: true,
                                    disabled: submitting
                                }
                            }}
                        />
                    </LocalizationProvider>
                );
            case 'number':
                return (
                    <TextField
                        id={field.field_name}
                        label={field.label}
                        type="number"
                        fullWidth
                        value={fieldValue || ''}
                        onChange={handleChange(field)}
                        required={Boolean(field.required)}
                        disabled={submitting}
                    />
                );
            case 'json': {
                // Handle JSON fields by displaying as pretty-printed JSON
                const jsonValue = typeof fieldValue === 'object' 
                    ? JSON.stringify(fieldValue, null, 2) 
                    : fieldValue || '';
                return (
                    <TextField
                        id={field.field_name}
                        label={field.label}
                        multiline
                        rows={6}
                        fullWidth
                        value={jsonValue}
                        onChange={handleChange(field)}
                        required={Boolean(field.required)}
                        disabled={submitting}
                        sx={{ fontFamily: 'monospace' }}
                        helperText="JSON data - edit with care"
                    />
                );
            }
            case 'image':
                return (
                    <ImageField
                        value={fieldValue || ''}
                        onChange={(url) => {
                            setFormData(prev => ({
                                ...prev,
                                [field.field_name]: url
                            }));
                        }}
                        label={field.label || field.field_name}
                        required={Boolean(field.required)}
                        disabled={submitting}
                    />
                );
            default: {
                if (isDateField && field.type !== 'date') {
                    return (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={field.label}
                                value={fieldValue ? new Date(fieldValue) : null}
                                onChange={handleDateChange(field)}
                                slotProps={{
                                    textField: {
                                        id: field.field_name,
                                        required: Boolean(field.required),
                                        fullWidth: true,
                                        disabled: submitting
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    );
                }
                // Handle any object values by stringifying them for display
                const displayValue = typeof fieldValue === 'object' && fieldValue !== null
                    ? JSON.stringify(fieldValue, null, 2)
                    : fieldValue || '';
                
                // Check if this field should have AI enhancement (text fields that are editable)
                const shouldShowAI = field.type === 'text' && !isReadOnly && !isIdField && field.ai_editable;
                
                if (shouldShowAI) {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                id={field.field_name}
                                label={field.label}
                                fullWidth
                                value={displayValue}
                                onChange={handleChange(field)}
                                required={field.required}
                                disabled={submitting}
                                multiline={typeof fieldValue === 'object' && fieldValue !== null}
                                rows={typeof fieldValue === 'object' && fieldValue !== null ? 4 : 1}
                                sx={typeof fieldValue === 'object' && fieldValue !== null ? { fontFamily: 'monospace' } : {}}
                            />
                            <AIEnhancementButton
                                table={tableName}
                                recordId={typeof formData.id === 'number' ? formData.id : (formData.id ? Number(formData.id) : undefined)}
                                fieldName={field.field_name}
                                currentValue={fieldValue || ''}
                                formData={formData}
                                onEnhancementResult={(enhancedContent) => handleAIFieldUpdate(field.field_name, enhancedContent)}
                                disabled={submitting}
                            />
                        </Box>
                    );
                }
                
                return (
                    <TextField
                        id={field.field_name}
                        label={field.label}
                        fullWidth
                        value={displayValue}
                        onChange={handleChange(field)}
                        required={field.required}
                        disabled={submitting}
                        multiline={typeof fieldValue === 'object' && fieldValue !== null}
                        rows={typeof fieldValue === 'object' && fieldValue !== null ? 4 : 1}
                        sx={typeof fieldValue === 'object' && fieldValue !== null ? { fontFamily: 'monospace' } : {}}
                    />
                );
            }
        }
    };

    // =============================================================================
    // LOADING & ERROR STATES
    // =============================================================================
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !viewConfig) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">
                    Error loading form: {(error as Error)?.message || 'Configuration not found'}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                    sx={{ mt: 2 }}
                >
                    Back to List
                </Button>
            </Box>
        );
    }

    // =============================================================================
    // MAIN RENDER
    // =============================================================================
    return (
        <Box>
            <Paper sx={{ width: '100%', mb: 2 }}>
                {!previewMode && (
                    <Toolbar 
                        sx={{ 
                            pl: { sm: 2 }, 
                            pr: { xs: 1, sm: 1 }, 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: { xs: '56px', sm: '64px' }
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                        }}>
                            {!isMobile && (
                                <Tooltip title={returnTable ? `Back to ${returnTable}` : "Back to List"}>
                                    <IconButton edge="start" onClick={handleCancel} sx={{ mr: 1 }}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Typography variant="h6" component="div" sx={{
                                fontSize: isMobile ? '1.1rem' : '1.25rem'
                            }}>
                                {formTitle}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1 }}>
                            {isMobile && (
                                <Tooltip title={returnTable ? `Back to ${returnTable}` : "Back to List"}>
                                    <IconButton 
                                        onClick={handleCancel}
                                        disabled={submitting}
                                        sx={{
                                            minWidth: '48px',
                                            minHeight: '48px',
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {showEmailButton && (
                                <Tooltip title="Generate Email">
                                    <IconButton
                                        onClick={() => setEmailGeneratorOpen(true)}
                                        disabled={submitting}
                                        color="primary"
                                        sx={{
                                            minHeight: isMobile ? '48px' : '40px',
                                        }}
                                    >
                                        <EmailIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="AI Enhance Record">
                                <IconButton
                                    onClick={() => setAiPanelOpen(true)}
                                    disabled={submitting}
                                    color="secondary"
                                    sx={{
                                        minHeight: isMobile ? '48px' : '40px',
                                    }}
                                >
                                    <AIIcon />
                                </IconButton>
                            </Tooltip>
                            {!isNew && (
                                <Tooltip title="Delete">
                                    <IconButton
                                        onClick={handleDelete}
                                        disabled={submitting || deleteRecord.isPending}
                                        color="error"
                                        sx={{
                                            minHeight: isMobile ? '48px' : '40px',
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Cancel">
                                <IconButton
                                    onClick={handleCancel}
                                    disabled={submitting}
                                    sx={{
                                        minHeight: isMobile ? '48px' : '40px',
                                    }}
                                >
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={submitting ? 'Saving...' : 'Save'}>
                                <IconButton
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    color="primary"
                                    sx={{
                                        minHeight: isMobile ? '48px' : '40px',
                                    }}
                                >
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Toolbar>
                )}
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {loadingOptions && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            <Typography>Loading field options...</Typography>
                        </Box>
                    )}
                    {(() => {
                        const validSections = Object.entries(sections).filter(([sectionId, section]) => {
                            const typedSection = section as Section;
                            // Filter out invalid sections and related list sections
                            if (!typedSection || !typedSection.title || !Array.isArray(typedSection.fields)) {
                                if (!sectionId.startsWith('related-')) {
                                    console.warn('[RecordForm] Invalid section structure:', { sectionId, section });
                                }
                                return false;
                            }
                            return true;
                        });

                        // Get related lists
                        let relatedLists: any[] = [];
                        if (!previewMode) {
                            relatedLists = layout?.layout_config?.related_lists || [];
                            if (!relatedLists && layout?.layout_config) {
                                try {
                                    const parsedConfig = typeof layout.layout_config === 'string' 
                                        ? JSON.parse(layout.layout_config) 
                                        : layout.layout_config;
                                    relatedLists = parsedConfig.related_lists || [];
                                } catch (error) {
                                    console.error('[RecordForm] Error parsing layout_config:', error);
                                    relatedLists = [];
                                }
                            }
                        }

                        const currentId = formData.id || existingRecord?.id;
                        const hasValidRecordId = currentId && typeof currentId === 'number';
                        const showRelatedLists = hasValidRecordId && relatedLists.length > 0;

                        // Always render form sections as regular stacked content
                        const formSectionsContent = (
                            <>
                                {validSections.map(([sectionId, section]) => {
                                    const typedSection = section as Section;
                                    return (
                                        <Box key={sectionId} sx={{ mb: 4 }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    mb: 2,
                                                    fontSize: isMobile ? '1.1rem' : '1.25rem'
                                                }}
                                            >
                                                {typedSection.title}
                                            </Typography>
                                            <MuiGrid container spacing={isMobile ? 2 : 3}>
                                                {typedSection.fields.map((field: FormField, index: number) => {
                                                    const fieldConfig = layout?.layout_config?.fields?.find((f: any) => f.name === field.field_name);
                                                    const fieldWidth = fieldConfig?.width ?? 100;
                                                    
                                                    // On mobile, most fields should be full width except checkboxes
                                                    const mobileSpan = field.type === 'boolean' ? 12 : 12;
                                                    const desktopSpan = fieldWidth <= 50 ? 6 : 12;
                                                    
                                                    return (
                                                      <MuiGrid
                                                          item
                                                          xs={mobileSpan}
                                                          sm={desktopSpan}
                                                          key={`${sectionId}-field-${field.field_name}-${index}`}
                                                      >
                                                          {renderField(field)}
                                                      </MuiGrid>
                                                    );
                                                })}
                                            </MuiGrid>
                                        </Box>
                                    );
                                })}
                            </>
                        );

                        // If no related lists, just return form sections
                        if (!showRelatedLists) {
                            return formSectionsContent;
                        }

                        // If we have related lists, render form sections + related list tabs
                        return (
                            <>
                                {/* Form sections as regular stacked content */}
                                {formSectionsContent}

                                {/* Related lists as tabs */}
                                <Box sx={{ mt: 4 }}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                        <Tabs 
                                            value={activeTab} 
                                            onChange={handleTabChange} 
                                            aria-label="related lists"
                                            variant={isMobile ? "scrollable" : "standard"}
                                            scrollButtons={isMobile ? "auto" : false}
                                        >
                                            {relatedLists.map((relatedList, index) => {
                                                const count = relatedListCounts[relatedList.id];
                                                const label = `${relatedList.title || relatedList.tableName}${count !== undefined ? ` (${count})` : ''}`;
                                                
                                                return (
                                                    <Tab 
                                                        key={`related-${relatedList.id}`}
                                                        label={label}
                                                        {...a11yProps(index)} 
                                                    />
                                                );
                                            })}
                                        </Tabs>
                                    </Box>

                                    {/* Related list tab panels */}
                                    {relatedLists.map((relatedList, index) => {
                                        const recordId = currentId as number;
                                        
                                        return (
                                            <TabPanel key={`related-${relatedList.id}`} value={activeTab} index={index}>
                                                <RelatedListDisplay
                                                    key={`related-${relatedList.id}-${recordId}`}
                                                    config={relatedList}
                                                    parentRecordId={recordId}
                                                    onAddRecord={() => {
                                                        // Build URL with pre-population and return path
                                                        const params = new URLSearchParams();
                                                        params.set(relatedList.relationshipField, recordId.toString());
                                                        params.set('returnTable', tableName);
                                                        params.set('returnId', recordId.toString());
                                                        
                                                        const url = `/${relatedList.tableName}/new?${params.toString()}`;
                                                        navigate(url);
                                                    }}
                                                    onEditRecord={(recordId) => {
                                                        // Build URL with return path for editing
                                                        const params = new URLSearchParams();
                                                        params.set('returnTable', tableName);
                                                        params.set('returnId', recordId.toString());
                                                        
                                                        const url = `/${relatedList.tableName}/${recordId}?${params.toString()}`;
                                                        navigate(url);
                                                    }}
                                                />
                                            </TabPanel>
                                        );
                                    })}
                                </Box>
                            </>
                        );
                    })()}
                </Box>
            </Paper>
            
            {/* AI Enhancement Panel */}
            <AIEnhancementPanel
                open={aiPanelOpen}
                onClose={() => setAiPanelOpen(false)}
                table={tableName}
                recordId={typeof formData.id === 'number' ? formData.id : (formData.id ? Number(formData.id) : undefined)}
                formData={formData}
                onFieldUpdate={handleAIFieldUpdate}
                onBulkUpdate={handleAIBulkUpdate}
                onSave={handleSubmit}
            />
            
            {/* Email Generator */}
            {showEmailButton && (
                <EmailGenerator
                    open={emailGeneratorOpen}
                    onClose={() => setEmailGeneratorOpen(false)}
                    guestId={typeof formData.id === 'number' ? formData.id : Number(formData.id)}
                    guestName={formData.name as string}
                />
            )}
        </Box>
    );
}
